import pacienteComoAdicionarImg from '@/assets/help/paciente-como-adicionar.png';
import pacienteDetalheImg from '@/assets/help/paciente-detalhe.png';
import pacienteFormularioImg from '@/assets/help/paciente-formulario.png';
import type { HelpArticle } from '../types';

export const PATIENTS_ARTICLES: HelpArticle[] = [
    {
        slug: 'cadastrar-pacientes',
        topicId: 'patients',
        title: 'Cadastre e gerencie seus pacientes',
        description:
            'As duas formas de adicionar um paciente: preenchendo os dados você mesmo ou enviando um link para o paciente responder.',
        updatedAt: '2026-07-03',
        keywords: ['paciente', 'cadastro', 'novo paciente', 'adicionar', 'editar', 'excluir'],
        blocks: [
            {
                type: 'paragraph',
                text: 'O cadastro de pacientes concentra tudo o que acontece com cada pessoa que você atende: laudos, testes, formulários e anamnese. Adicionar um paciente leva menos de um minuto.',
            },
            { type: 'subheading', id: 'duas-formas', text: 'Escolha como adicionar' },
            {
                type: 'paragraph',
                text: 'Ao clicar em **Novo paciente**, na página **Pacientes**, você escolhe entre duas formas: **enviar um link de formulário** para o próprio paciente preencher os dados ou **inserir manualmente**.',
            },
            {
                type: 'image',
                src: pacienteComoAdicionarImg,
                alt: 'Modal com as duas formas de adicionar paciente: enviar link de formulário ou inserir manualmente',
            },
            { type: 'subheading', id: 'manual', text: 'Cadastro manual' },
            {
                type: 'steps',
                items: [
                    'Escolha **Inserir manualmente** e clique em **Continuar**.',
                    'Preencha o **nome completo**, a **data de nascimento** e o **sexo**. A idade é calculada automaticamente.',
                    'Se quiser, complete os demais campos: profissional solicitante, escolaridade, contato e endereço.',
                    'Clique em **Criar Paciente**.',
                ],
            },
            {
                type: 'image',
                src: pacienteFormularioImg,
                alt: 'Formulário Adicionar Novo Paciente com os campos de informações básicas',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'Logo após criar o paciente, a plataforma já oferece o atalho para atribuir testes e formulários. Aproveite o embalo se a avaliação já vai começar.',
            },
            { type: 'subheading', id: 'gerenciar', text: 'Gerenciando depois' },
            {
                type: 'paragraph',
                text: 'Na lista de pacientes você busca por nome, abre o cadastro para editar qualquer dado e acompanha o que já foi feito com cada um. Para ver tudo de um paciente em um só lugar, abra a página dele, apresentada no guia **A página do paciente**.',
            },
        ],
    },
    {
        slug: 'pre-anamnese-pelo-paciente',
        topicId: 'patients',
        title: 'Deixe o paciente preencher a pré-anamnese',
        description:
            'Envie um link seguro e receba o cadastro completo com uma pré-anamnese pronta, sem digitar nada.',
        updatedAt: '2026-09-07',
        keywords: ['pré-anamnese', 'link', 'formulário', 'cadastro automático', 'anamnese'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Digitar os dados do paciente na mão funciona, mas dá para fazer melhor. Com o link de formulário, o próprio paciente ou o responsável preenche as informações, e o LaudoPsi monta o cadastro para você.',
            },
            { type: 'subheading', id: 'como-enviar', text: 'Como enviar o link' },
            {
                type: 'steps',
                items: [
                    'Na página **Pacientes**, clique em **Novo paciente**.',
                    'Escolha a opção **Enviar link de formulário**.',
                    'Digite o nome do paciente e clique em **Enviar link**.',
                    'Compartilhe o endereço gerado por WhatsApp ou e-mail.',
                ],
            },
            { type: 'subheading', id: 'o-que-acontece', text: 'O que acontece depois' },
            {
                type: 'paragraph',
                text: 'Quando o paciente responde, o cadastro é preenchido automaticamente com os dados informados. E tem um bônus importante: você recebe uma **pré-anamnese** com informações que ajudam na avaliação, pronta para ser usada como insumo nas seções do laudo geradas com IA.',
            },
            {
                type: 'callout',
                tone: 'info',
                text: 'O formulário certo é escolhido conforme o caso. Para menores de idade existe o formulário de perguntas aos pais ou cuidadores, e para adultos a pré-anamnese adulto. Veja mais na seção Testes e formulários.',
            },
            {
                type: 'callout',
                tone: 'warning',
                text: 'O link enviado ao paciente é seguro e expira em 30 dias. Se vencer antes da resposta, é só gerar um novo.',
            },
        ],
    },
    {
        slug: 'pagina-do-paciente',
        topicId: 'patients',
        title: 'A página do paciente: histórico e anamnese',
        description:
            'Acompanhe laudos, testes e formulários de cada paciente e escreva a anamnese com modelos prontos.',
        updatedAt: '2026-07-03',
        keywords: ['página do paciente', 'histórico', 'anamnese', 'modelos', 'acompanhamento'],
        blocks: [
            {
                type: 'paragraph',
                text: 'Clique em um paciente na lista para abrir a página dele. É o retrato completo do caso: quantos formulários e testes foram enviados, quais laudos existem e qual o status da anamnese.',
            },
            {
                type: 'image',
                src: pacienteDetalheImg,
                alt: 'Página do paciente com os contadores de formulários, testes e laudos e as abas de histórico',
            },
            { type: 'subheading', id: 'acoes-rapidas', text: 'Ações rápidas' },
            {
                type: 'paragraph',
                text: 'No topo da página você tem os botões **Enviar formulário**, **Enviar teste** e **Novo Laudo**, além do atalho para editar o cadastro. As abas abaixo mostram cada laudo, cada resposta de formulário e cada teste do paciente.',
            },
            { type: 'subheading', id: 'anamnese', text: 'A anamnese dentro do LaudoPsi' },
            {
                type: 'paragraph',
                text: 'Na aba **Anamnese** você escreve a anamnese do paciente em um editor de texto completo. Ao começar, escolha entre **Sem modelo**, para uma página em branco, e **Com modelo** — nesse caso o LaudoPsi já sugere o modelo certo (**infantil**, **adolescente** ou **adulto**) a partir da data de nascimento cadastrada, e você pode trocar por outro quando quiser.',
            },
            {
                type: 'paragraph',
                text: 'Você também pode começar uma anamnese sem passar pela página do paciente: no menu lateral, clique em **Criar** e escolha **Nova anamnese** — basta buscar o paciente e seguir pelas mesmas opções.',
            },
            {
                type: 'callout',
                tone: 'success',
                text: 'A anamnese registrada aqui vira insumo direto para a IA nas seções do laudo, como Descrição da Demanda e História Clínica. Uma anamnese completa significa rascunhos muito melhores.',
            },
        ],
    },
];
